import { useDispatch, useSelector } from 'react-redux'
import { Button } from '../../../Components/Button/Button'
import { useLocalization } from '../../../Hooks/useLocalization'
import './CompanyList.scss'
import { setDestinationView } from '../../../Store/InterfaceSlice'
import { TextInput } from '../../../Components/TextInput/TextInput'
import { type RootState } from '../../../Store/Store'
import { useEffect, useMemo, useState } from 'react'
import {
  setIsCertificatesRequested,
  setIsCompaniesRequested,
} from '../../../Store/DataSlice'
import { CompaniesTable } from '../CompaniesTable'
import { sortResultString } from '../../../Utils/Arrays'

const localizationSection = 'company'
export function CompanyList() {
  const dispatch = useDispatch()
  const localization = useLocalization(localizationSection)
  const user = useSelector((state: RootState) => state.authentication.user)
  const isLoggedIn = !!user
  const isAdmin = user?.isAdmin ?? false

  // companies
  const isCompaniesRequested = useSelector(
    (state: RootState) => state.data.isCompaniesRequested
  )
  useEffect(() => {
    if (!isCompaniesRequested) dispatch(setIsCompaniesRequested(true))
  }, [isCompaniesRequested, dispatch])
  const allCompanies = useSelector((state: RootState) => state.data.companies)

  // certificates
  const isCertificatesRequested = useSelector(
    (state: RootState) => state.data.isCertificatesRequested
  )
  useEffect(() => {
    if (!isCertificatesRequested) dispatch(setIsCertificatesRequested(true))
  }, [isCertificatesRequested, dispatch])
  const allCertificates = useSelector(
    (state: RootState) => state.data.enrichedCertificates
  )

  // certificateCount per company vat
  const certificateCountPerCompanyVat = useMemo(() => {
    const result: Record<string, number> = {}
    for (const certificate of allCertificates) {
      for (const companyBranch of certificate.companyBranches) {
        const vatNumber = companyBranch.vatNumber
        if (result[vatNumber] == null) result[vatNumber] = 0
        result[vatNumber] += 1
      }
    }
    return result
  }, [allCertificates])

  const [filterVatNumber, setFilterVatNumber] = useState('')
  const [filterName, setFilterName] = useState('')

  const filteredCompanies = useMemo(() => {
    return allCompanies
      .filter((c) => {
        if (
          filterVatNumber &&
          !c.vatNumber.toLowerCase().includes(filterVatNumber.toLowerCase())
        )
          return false
        if (
          filterName &&
          !c.name.toLowerCase().includes(filterName.toLowerCase())
        )
          return false
        if (!isLoggedIn && certificateCountPerCompanyVat[c.vatNumber] == null)
          return false
        return true
      })
      .sort((a, b) => sortResultString(a.vatNumber, b.vatNumber))
  }, [
    allCompanies,
    filterVatNumber,
    filterName,
    isLoggedIn,
    certificateCountPerCompanyVat,
  ])

  return (
    <div className="company-list">
      {isLoggedIn ? (
        <div className="button-row">
          <Button
            importance={'primary'}
            text={localization.text('add-company', 'Voeg onderneming toe')}
            onClick={() =>
              dispatch(
                setDestinationView({
                  destinationView: 'company-add',
                })
              )
            }
          />
        </div>
      ) : null}
      <div className="filters">
        <div className="filter-field">
          <TextInput
            label={localization.text(
              'filter-vat-number-input-label',
              'Ondernemingsnummer'
            )}
            helperText={localization.text(
              'filter-vat-number-input-helper',
              'Geef (een deel van) de het ondernemingsnummer in'
            )}
            value={filterVatNumber}
            onChange={setFilterVatNumber}
          />
        </div>
        <div className="filter-field">
          <TextInput
            label={localization.text(
              'filter-company-input-label',
              'Onderneming'
            )}
            helperText={localization.text(
              'filter-company-input-helper',
              'Geef (een deel van) de naam van de onderneming in'
            )}
            value={filterName}
            onChange={setFilterName}
          />
        </div>
      </div>
      <CompaniesTable
        companies={filteredCompanies}
        isLoading={allCompanies.length === 0}
        showAdminColumns={isAdmin}
      />
    </div>
  )
}
